import React, { useState, useRef, useEffect } from 'react';
import { useApolloClient } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useFormikContext } from 'formik';
import {
  callNativeAppRemoveLastRoute,
  callNativeMobileApp,
  useToast,
  useAppContext,
} from '@front-activerse/common-components';
import { Button, Container, Row, Col } from 'reactstrap';
import { useTranslator } from '@front-activerse/common-components';
import { FormikCheckboxInput as Checkbox } from '@front-activerse/common-components';
import { UserFlagIdentifierType } from '@front-activerse/common-components';
import createRequest from './helpers/createRequest';

interface IAcceptancePage {
  generateDiet: (values: any) => void;
  setUserFlag: (identifier: UserFlagIdentifierType) => Promise<any>;
  dietTypes: any;
  dietLevels: any;
}

const AcceptancePage: React.FC<IAcceptancePage> = ({
  generateDiet,
  setUserFlag,
  dietTypes,
  dietLevels,
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const client = useApolloClient();
  const { toastError } = useToast();
  const { t } = useTranslator();
  const { values, validateField, setFieldError, setFieldTouched, resetForm } =
    useFormikContext<any>();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setFieldError('acceptance', undefined);
  }, []);

  const {
    consts: { SLIDE_ROUTES_DURATION, APP_BRAND },
    images: { backIcon, dietWarningImg },
  } = useAppContext();

  const scrollToTopAndNavigate = (url: string) => {
    const el = document.querySelector('.layout') as HTMLInputElement;
    if (el.scrollTop) {
      el.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => {
        navigate(url);
      }, Number(SLIDE_ROUTES_DURATION));
    } else {
      navigate(url);
    }
  };

  const renderButton = () => (
    <Button color="primary" className="w-100" onClick={handleSubmit} disabled={isLoading}>
      {isLoading ? (
        <>
          <span
            className="spinner-border spinner-border-sm me-2"
            role="status"
            aria-hidden="true"
          />{' '}
          <span>{t('data-processing')}</span>
        </>
      ) : (
        t('app.goalSettings.dietWarning.confirmButton')
      )}
    </Button>
  );

  const handleSubmit = async (event: any) => {
    event.preventDefault();

    validateField('acceptance');
    setFieldTouched('acceptance', true);

    if (values.acceptance) {
      setIsLoading(true);

      try {
        const response = await setUserFlag(UserFlagIdentifierType.DIET_WARNING_SEEN);
        if (response.data.me.flag.set.__typename === 'BasicMutationSuccess') {
          try {
            const res: any = await generateDiet(
              createRequest(values, dietTypes, dietLevels, String(APP_BRAND))
            );
            if (res.__typename === 'BasicMutationSuccess') {
              resetForm();
              callNativeMobileApp({
                action: 'refresh_data',
                payload: {
                  module: 'diet_settings',
                },
              });
              client.refetchQueries({ include: ['DietSettings'] });
              scrollToTopAndNavigate(`/diet-settings/all/loader`);
            }
          } catch (error) {
            toastError();
          } finally {
            setIsLoading(false);
          }
        }
      } catch (error) {
        toastError();
      }
    } else {
      inputRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    navigate(-1);
    callNativeAppRemoveLastRoute();
  };

  return (
    <>
      <div className="full-height">
        <Container className="py-4">
          <Row>
            <Col xs={12} sm={{ size: 6, offset: 3 }}>
              <Row className="d-sm-none">
                <Col xs={{ size: 3 }} className="d-md-none">
                  <Button color="" className="my-md-3 px-0" onClick={handleBack}>
                    <img src={backIcon} alt={t('back.button')} />
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={{ size: 6, offset: 3 }}>
              <div className="text-center">
                <img src={dietWarningImg} className="img-fluid mb-3" alt="" />
              </div>
              <h2 className="title-large semi-bold text-center">
                {t('app.goalSettings.dietWarning.title')}
              </h2>
              <p className="text-text-variant-color">
                {t('app.goalSettings.dietWarning.description.part1')}
              </p>
              <p className="text-text-variant-color">
                {t('app.goalSettings.dietWarning.description.part2')}
              </p>
              <p className="text-text-variant-color">
                {t('app.goalSettings.dietWarning.description.part3')}
              </p>
              <p className="text-text-variant-color">
                {t('app.goalSettings.dietWarning.description.part4')}
              </p>
              <div className="acceptance-box">
                <Checkbox name="acceptance" innerRef={inputRef}>
                  <p className="mb-0 paragraph-text-primary small">
                    {t('app.goalSettings.dietWarning.checkboxDeclaration')}
                  </p>
                </Checkbox>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
      <div className="sticky-footer">
        <Container>
          <Row>
            <Col md={{ size: 6, offset: 3 }}>
              <div className="d-none d-md-block">
                <div className="d-flex position-relative">
                  <Button color="secondary" outline className="w-100 me-3" onClick={handleBack}>
                    {t('back.button')}
                  </Button>
                  {renderButton()}
                </div>
              </div>
              <div className="d-md-none">{renderButton()}</div>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default AcceptancePage;
